import React, { useState, useRef, useMemo, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import axios from 'axios';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { TRANSLATIONS } from '../../../translations';
import { BREAKPOINTS } from '../../../constants';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import { ModalDrawerResponse } from './ModalDrawerResponse';
import { ModalDrawerExitFlow } from './ModalDrawerExitFlow';
import { MegaSelect } from './MegaSelect';
import { ModalNotCompletedToVoucher } from './ModalNotCompletedToVoucher';
import { ModalNotCompletedReschedule } from './ModalNotCompletedReschedule';
import { ModalNotCompletedToGiftcard } from './ModalNotCompletedToGiftcard';
import { CompleteRefundOnHold } from './subComponents/CompleteRefundOnHold';
import { CompleteRefundConfirmed } from './subComponents/CompleteRefundConfirmed';
import { getActionlist, getRequestType } from '../../hooks/useOrderListUtils';
import { ModalOrderCard } from './subComponents/ModalOrderCard';

const REFUND = 'refund';
//TODO: add other actions
const RESCHEDULE = 'reschedule';
const TRANSFORM_TO_VOUCHER = 'transformToVoucher';
const TRANSFORM_TO_GIFTCARD = 'transformToGiftcard';
const modalTitle = TRANSLATIONS.CANCEL_RESERVATION;
//TODO: to check
const REQUEST_LABEL = 'refund';

//NotCompleted = onHold and confirmed orders
const ModalDeleteBookingNotCompleted = ({
  isOpen,
  setIsOpen,
  token,
  order,
  setRequestDetails,
}) => {
  if (!order || !token) return null;

  const hasMounted = useRef(false);
  const orderId = order.id;
  const orderStatus = order.status;
  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );

  const [step, setStep] = useState(1);
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    isSuccess: false,
    responseContent: '',
    modalTitle: '',
  });

  const [megaSelectError, setMegaSelectError] = useState(null);
  const [action, setAction] = useState(null);
  const [openRefundModal, setOpenRefundModal] = useState(false);
  const [openVoucherModal, setOpenVoucherModal] = useState(false);
  const [openGiftcardModal, setOpenGiftcardModal] = useState(false);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [showModalExit, setShowModalExit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const requestType = getRequestType(order.status, REQUEST_LABEL);

  const actionsList = getActionlist(order);

  if (orderStatus.includes('confirmed')) {
    if (actionsList[TRANSFORM_TO_VOUCHER]) {
      actionsList[TRANSFORM_TO_VOUCHER].recommended = true;
    } else if (actionsList[TRANSFORM_TO_GIFTCARD]) {
      actionsList[TRANSFORM_TO_GIFTCARD].recommended = true;
    }
  }
  if (orderStatus.includes('on_hold')) {
    if (actionsList[RESCHEDULE]) {
      actionsList[RESCHEDULE].recommended = true;
    }
  }

  // reset isOpen status if the action is interrupted in any of these flows
  useEffect(() => {
    if (hasMounted.current) {
      if (
        !openRescheduleModal &&
        !openVoucherModal &&
        !openGiftcardModal &&
        !openRefundModal
      ) {
        setIsOpen(false);
      }
    } else {
      hasMounted.current = true;
    }
  }, [
    openRescheduleModal,
    openVoucherModal,
    openGiftcardModal,
    openRefundModal,
  ]);

  useEffect(() => {
    document.body.classList.toggle('noscroll', isOpen);
    return () => document.body.classList.remove('noscroll');
  }, [isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      (!Object.keys(actionsList).length ||
        Object.keys(actionsList).length === 1)
    ) {
      setStep(2);
      setAction(REFUND);
      setOpenRefundModal(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      resetErrors();
      setShowModalExit(false);
    }
  }, [isOpen]);

  const proceed = () => {
    if (step === 1) {
      if (!action) {
        setMegaSelectError(TRANSLATIONS.RADIO_LIST_ERROR);
        return;
      } else {
        setMegaSelectError(null);
        setOpenRefundModal(action === REFUND);
        setOpenVoucherModal(action === TRANSFORM_TO_VOUCHER);
        setOpenGiftcardModal(action === TRANSFORM_TO_GIFTCARD);
        setOpenRescheduleModal(action === RESCHEDULE);
      }
    }

    setStep(step + 1);
  };

  const handleGoBack = () => {
    setStep(step - 1);
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      const { data } = await axios.post(
        `${endpoints.core}/order-customer-request/store`,
        {
          order_id: orderId,
          type: requestType,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let requestDetails = data && data.order ? data.order : null;
      setRequestDetails(requestDetails);
      setResponseModal({
        isOpen: true,
        isSuccess: true,
        responseContent:
          TRANSLATIONS.YOUR_REQUEST_TO_CANCEL_THE_ACTIVITY_HAS_BEEN_SUCCESSFULLY_SUBMITTED,
        modalTitle: modalTitle,
      });
    } catch (error) {
      // console.log(error);
      setResponseModal({
        isOpen: true,
        isSuccess: false,
        responseContent:
          TRANSLATIONS.UNEXPECTED_ERROR_HAS_OCCURRED_PLEASE_RETRY_LATER,
        modalTitle: modalTitle,
      });
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const resetErrors = () => {
    setTimeout(() => {
      setMegaSelectError(false);
    }, 500);
  };

  const firstStepModal = (
    <ModalDrawer
      isOpen={isOpen && step === 1 && !showModalExit}
      setIsOpen={setIsOpen}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={modalTitle}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-m">
          <ModalOrderCard order={order} noBorder />
          <Typography
            classNames="text-left"
            variant={TypographyVariants.p3}
            weight={TypographyWeights.bold}
          >
            {TRANSLATIONS.DONT_GIVE_UP_DISCOVER_OUR_ALTERNATIVES}
          </Typography>
          <MegaSelect
            options={actionsList}
            defaultValue={null}
            onChange={(value) => {
              if (value === action) {
                setAction(null);
              } else {
                setAction(value);
              }
            }}
            error={megaSelectError}
            setError={setMegaSelectError}
          />
        </div>
      }
      footer={
        <div className="d-flex gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={TRANSLATIONS.CANCEL}
            onClick={() => {
              setShowModalExit(true);
            }}
          />
          <Button
            classNames={'flex-1 pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.secondary}
            label={TRANSLATIONS.CONTINUE}
            onClick={() => proceed()}
          />
        </div>
      }
      handleAfterClose={resetErrors}
    />
  );

  const completeRefundRequest = order.status.includes('on_hold') ? (
    <CompleteRefundOnHold
      goBack={() => setStep(step - 1)}
      isOpen={openRefundModal}
      setIsOpen={setOpenRefundModal}
      modalTitle={modalTitle}
      order={order}
      isLoading={isLoading}
      onSubmit={() => onSubmit()}
    />
  ) : order.status.includes('confirmed') ? (
    <CompleteRefundConfirmed
      goBack={() => setStep(step - 1)}
      isOpen={openRefundModal}
      setIsOpen={setOpenRefundModal}
      modalTitle={modalTitle}
      order={order}
      isLoading={isLoading}
      onSubmit={() => onSubmit()}
      isOnlyChoice={!Object.keys(actionsList).length}
    />
  ) : null;

  const voucherModal = (
    <ModalNotCompletedToVoucher
      isOpen={openVoucherModal}
      setIsOpen={setOpenVoucherModal}
      order={order}
      token={token}
      customGoBackFunc={handleGoBack}
      setResponseModal={setResponseModal}
      setRequestDetails={setRequestDetails}
    />
  );

  const giftcardModal = (
    <ModalNotCompletedToGiftcard
      isOpen={openGiftcardModal}
      setIsOpen={setOpenGiftcardModal}
      order={order}
      token={token}
      customGoBackFunc={handleGoBack}
      setResponseModal={setResponseModal}
      setRequestDetails={setRequestDetails}
    />
  );

  const rescheduleModal = (
    <ModalNotCompletedReschedule
      isOpen={openRescheduleModal}
      setIsOpen={setOpenRescheduleModal}
      order={order}
      token={token}
      customGoBackFunc={handleGoBack}
      setResponseModal={setResponseModal}
      setRequestDetails={setRequestDetails}
    />
  );

  const responseModalDrawer = (
    <ModalDrawerResponse
      isOpen={responseModal.isOpen}
      setIsOpen={setResponseModal}
      isSuccess={responseModal.isSuccess}
      modalTitle={responseModal.modalTitle}
      responseContent={responseModal.responseContent}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
    />
  );

  return (
    <>
      {responseModalDrawer}
      {firstStepModal}
      {step === 2 && openRefundModal && completeRefundRequest}
      {step === 2 && openVoucherModal && voucherModal}
      {step === 2 && openGiftcardModal && giftcardModal}
      {step === 2 && openRescheduleModal && rescheduleModal}

      <ModalDrawerExitFlow
        isOpen={showModalExit}
        setIsOpen={setShowModalExit}
        closeAction={() => setIsOpen(false)}
        isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      />
    </>
  );
};

ModalDeleteBookingNotCompleted.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
  customGoBackFunc: PropTypes.func,
  setResponseModal: PropTypes.func,
};

export { ModalDeleteBookingNotCompleted };
